/**
 * Shared minimized cards + general avatar for action log tooltip and drawer rows.
 */
import React, { useMemo } from 'react';
import OrderDesign from '../OrderDesign';
import { findInGameState } from '../actionHistoryManager';
import { getUnitFromRegistry } from '../UnitRegistry';
import { getAvatarImage, getOpponentAvatarImage } from '../avatars';

/** Fill display fields on a log snapshot without replacing identity fields from a fuller order. */
function mergeMissingDisplayFieldsFromLive(snapshot, liveOrder) {
  if (!liveOrder) return snapshot;
  const merged = { ...snapshot };
  if (merged.attack_rating == null && liveOrder.attack_rating != null) {
    merged.attack_rating = liveOrder.attack_rating;
  }
  if (merged.health == null && liveOrder.health != null) {
    merged.health = liveOrder.health;
  }
  if ((merged.cost == null || merged.cost === 0) && liveOrder.cost != null) {
    merged.cost = liveOrder.cost;
  }
  if ((!merged.abilities || merged.abilities.length === 0) && Array.isArray(liveOrder.abilities) && liveOrder.abilities.length > 0) {
    merged.abilities = liveOrder.abilities;
  }
  if (!merged.description && typeof liveOrder.description === 'string' && liveOrder.description.length > 0) {
    merged.description = liveOrder.description;
  }
  if (!merged.type && liveOrder.type) {
    merged.type = liveOrder.type;
  }
  if (!merged.type_display && liveOrder.type_display) {
    merged.type_display = liveOrder.type_display;
  }
  if (!merged.image && liveOrder.image) {
    merged.image = liveOrder.image;
  }
  if (!merged.faction_name && liveOrder.faction_name) {
    merged.faction_name = liveOrder.faction_name;
  }
  if (!merged.faction && liveOrder.faction) {
    merged.faction = liveOrder.faction;
  }
  return merged;
}

export function resolveOrderForDesign(gameState, unitLike, unitRegistryRef = null) {
  if (!unitLike) return null;

  const snapshot = {
    id: unitLike.id,
    name: unitLike.name || 'Unknown',
    faction_name: unitLike.faction_name || unitLike.faction || 'League of Shadows',
    cost: unitLike.cost ?? 0,
    abilities: unitLike.abilities || [],
    attack_rating: unitLike.attack_rating,
    health: unitLike.health,
    description: unitLike.description,
    type: unitLike.type,
    type_display: unitLike.type_display,
    image: unitLike.image,
  };

  let liveOrder = null;
  if (unitLike.instanceId && gameState?.state) {
    const found = findInGameState(gameState, unitLike.instanceId);
    if (found?.order) liveOrder = found.order;
  }

  let registryOrder = null;
  if (unitLike.instanceId && unitRegistryRef) {
    registryOrder = getUnitFromRegistry(unitLike.instanceId, unitRegistryRef);
  }

  // Action log entries are historical — prefer embedded log snapshot for identity/image
  // (avoids thumbnail flicker on websocket updates). Fill missing card text/stats from
  // live gameState, then unit registry (same orders already rendered in battle).
  if (snapshot.image || (snapshot.name && snapshot.name !== 'Unknown')) {
    let merged = mergeMissingDisplayFieldsFromLive(snapshot, liveOrder);
    merged = mergeMissingDisplayFieldsFromLive(merged, registryOrder);
    return merged;
  }

  if (liveOrder) return mergeMissingDisplayFieldsFromLive({ ...snapshot, ...liveOrder }, registryOrder);
  if (registryOrder) return mergeMissingDisplayFieldsFromLive(snapshot, registryOrder);
  return snapshot;
}

export function useResolvedOrderForDesign(order, gameState, unitRegistryRef = null) {
  return useMemo(
    () => resolveOrderForDesign(gameState, order, unitRegistryRef),
    [gameState, order, unitRegistryRef]
  );
}

/** One minimized order card; optional skull overlay. zoneName prevents OrderDesign translateY(40px). */
export function TooltipOrderCard({ order, gameState, unitRegistryRef, showSkull, zoneName = 'tooltip' }) {
  const resolved = useResolvedOrderForDesign(order, gameState, unitRegistryRef);

  if (!resolved) return null;
  return (
    <div className="bal-tooltip-order-wrap">
      <div className="bal-tooltip-order-inner">
        <OrderDesign order={resolved} count={0} minimal zoneName={zoneName} />
      </div>
      {showSkull && (
        <div className="bal-tooltip-kill-overlay" aria-hidden>💀</div>
      )}
    </div>
  );
}

/** Fixed-position expanded card for drawer thumbnail hover (same resolution path as TooltipOrderCard). */
export function BattleActionLogExpandedPreview({
  orderLike,
  gameState,
  unitRegistryRef,
  userId,
  fixedDesktopExpandedPosition,
  orderDesignScale = 1
}) {
  const resolved = useResolvedOrderForDesign(orderLike, gameState, unitRegistryRef);
  if (!resolved || !fixedDesktopExpandedPosition) return null;
  return (
    <div className="battle-action-log-drawer-expand-anchor">
      <OrderDesign
        order={resolved}
        count={0}
        minimal={false}
        gameState={gameState}
        userId={userId}
        zoneName="tooltip"
        fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
        scale={orderDesignScale}
        hideExpandedPortalSourceSlot
      />
    </div>
  );
}

/** General avatar for a player id (same logic as tooltip opponent branch). */
export function TooltipGeneralAvatar({ targetPlayerId, gameState, userId, opponentId }) {
  if (!gameState || targetPlayerId == null) return null;
  const isAutobattlerMode = gameState.isAutobattler || gameState.devAutobattlerMode;
  const isSpectatorMode = gameState.spectatorUserId && gameState.spectatorUserId === userId;
  const shouldUsePlayerAvatars = isAutobattlerMode || isSpectatorMode;

  if (String(targetPlayerId) === String(opponentId)) {
    const opponentAvatars = shouldUsePlayerAvatars
      ? (gameState.playerAvatars?.[targetPlayerId] || {})
      : (gameState.opponentAvatars?.[userId] || {});
    const opponentState = gameState.state?.[targetPlayerId];
    const opponentGeneral = opponentAvatars.general || opponentState?.selectedGeneral || gameState.opponentGeneral || '';
    const src = getOpponentAvatarImage(opponentGeneral);
    return (
      <div className="bal-tooltip-general-wrap">
        <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
      </div>
    );
  }
  const playerAvatars = gameState.playerAvatars?.[targetPlayerId] || {};
  const playerState = gameState.state?.[targetPlayerId];
  const playerGeneral = playerAvatars.general || playerState?.selectedGeneral || '';
  const src = getAvatarImage(playerGeneral);
  return (
    <div className="bal-tooltip-general-wrap">
      <img src={src} alt="" className="bal-tooltip-general-avatar" aria-hidden />
    </div>
  );
}
